import moment from 'moment';

/**
 * UI responses map to FSRS as 0=Again, 1=Hard, 2=Good, and 3=Easy. In
 * two-button mode, Wrong is a failed recall (Again); in four-button mode,
 * the same slot is Hard.
 */
export const wrongResponseFor = (
  responseOptions: string | undefined
): number => (responseOptions === 'four' ? 1 : 0);

/** One rating button: what it says, and the UI response it sends. */
export interface FlashcardRatingButton {
  response: number;
  label: string;
}

const FOUR_BUTTON_RATINGS: readonly FlashcardRatingButton[] = [
  { response: 0, label: 'Again' },
  { response: 1, label: 'Hard' },
  { response: 2, label: 'Good' },
  { response: 3, label: 'Easy' },
];

const TWO_BUTTON_RATINGS: readonly FlashcardRatingButton[] = [
  { response: wrongResponseFor('two'), label: 'Wrong' },
  { response: 2, label: 'Right' },
];

/**
 * The rating buttons for a student's chosen mode, in display order. The full
 * study surface builds the same set by hand; anything that rates a card outside
 * it (the in-chat study-set deck) reads it from here so the labels and the
 * grades behind them can never drift apart.
 */
export const ratingButtonsFor = (
  responseOptions: string | undefined
): readonly FlashcardRatingButton[] =>
  responseOptions === 'four' ? FOUR_BUTTON_RATINGS : TWO_BUTTON_RATINGS;

/**
 * How long until a card next comes back, as the rating buttons say it: minutes
 * under the hour, then hours, then days. The full flashcard screen and the
 * in-chat study-set deck both label their buttons from here, so the two rating
 * rows cannot say different things about the same step.
 */
export const formatFlashcardInterval = (
  due: Date | string,
  now: Date
): string => {
  const minutes = moment(due).diff(now, 'minutes');
  if (minutes < 60) return `${minutes}m`;
  if (minutes < 1440) return `${Math.floor(minutes / 60)}h`;

  return `${Math.floor(minutes / 1440)}d`;
};
